// "Add your design to more gifts": a small strip in the check dialog (confirm.js). It used to be a step of its own between Done and
// the check; since 2 Nov 2026 (Ryan) nothing stands between Done and the check.
//
// The strip shows the gifts of the product's shape (catalog/gifts.json, up to six), each with the customer's front design already placed
// on it (place.js), but only the gifts where the design fits well: a print area of about the same shape as the product's (GIFT_FIT).
// The customer ticks the ones they want, picks a size, and can tap Adjust to change how the design sits on a gift (the full editor in a
// frame, adjust.js). The check dialog saves each ticked gift as its own design when the customer confirms (selected()).
//
// Only for one person (never for a bulk order), only when the page can send the customer back to the shop, only when the
// front has a design, and only for a product whose shape has gifts. In any other case the strip stays hidden. A gift that cannot be
// loaded is left out; the strip never blocks the customer.
//
// Prices and sizes come live from the shop (store-sizes.js). If the shop cannot be asked, a card has no price and no size choice.

import { $, esc, shortTitle } from "./util.js";
import { giftsFor, loadGiftProduct, loadShape, buildCatalogProduct } from "./catalog.js";
import { placeDesign, countObjects } from "./place.js";
import { loadStoreOffer } from "./store-sizes.js";
import { formatPrice, giftDiscount } from "./bulk.js";
import { openAdjust } from "./adjust.js";

// A gift is shown only when the design fits it well (Ryan, 2 Nov 2026):
//   - the shape of its print area is close to the product's: the narrower over the wider of the two width/height ratios is at least
//     GIFT_FIT (a tall luggage cover and a very wide pencil pouch do not match);
//   - its print area is mostly solid: the print line covers at least GIFT_SOLID of the box around it (a U-shaped neck pillow, a moon
//     cushion or a bandana would cut the photo up).
export const GIFT_FIT = 0.5;
export const GIFT_SOLID = 0.65;
export function shapesMatch(a, b) {
  const ra = a.w / a.h, rb = b.w / b.h;
  return ra > 0 && rb > 0 && Math.min(ra, rb) / Math.max(ra, rb) >= GIFT_FIT && !(b.solid < GIFT_SOLID);
}
// The share of its box that a print line covers (0 to 1).
export function solidShare(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let a = 0, x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  points.forEach(([x, y], i) => {
    const [nx, ny] = points[(i + 1) % points.length];
    a += x * ny - nx * y;
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  });
  const box = (x1 - x0) * (y1 - y0);
  return box > 0 ? Math.abs(a) / 2 / box : 0;
}
// The box of a gift's first print line and how solid it is, from its shape file (no picture is loaded). null when it cannot be read.
async function giftArea(gift) {
  try {
    const shape = await loadShape(gift.shape);
    const area = buildCatalogProduct({ ...shape, id: gift.shape }, gift.handle).sides[0].area;
    const [x0, y0, x1, y1] = area.bbox;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0, solid: solidShare(area.points) };
  } catch (e) { return null; }
}

// editor: the editor of the main design. ctx: { shop, product, handle, variant, returnUrl }.
// onChange(): the ticks, a size or an adjusted gift changed (the check dialog shows the order again).
export function initMoreGifts({ editor, ctx, onChange = () => {} }) {
  const strip = $("revGifts"), grid = $("giftGrid");
  const shape = () => (editor.product && editor.product.shape) || "";
  let state = null;                  // { key, cards }: kept while the main design does not change, so ticks, sizes and adjusted designs stay
  let adjusting = null;
  const alive = (card) => !!state && state.cards.includes(card);
  const offers = new Map();          // handle -> the shop's answer (kept when it came; a failed answer is asked for again)
  const getOffer = async (handle) => {
    if (offers.has(handle)) return offers.get(handle);
    const o = await loadStoreOffer({ shop: ctx.shop, handle });
    if (o) offers.set(handle, o);
    return o;
  };

  // The step is offered for one person, with a way back to the shop (a return link, or the layer over the store page) and a shop to ask for sizes and prices, on a product that has gifts.
  const eligible = () => (!!ctx.returnUrl || !!ctx.embed) && !!ctx.shop && !!shape() && editor.bulk.mode() === "single";

  // Starts loading the list of gifts while the customer is still designing, so the step opens at once.
  function preload() { if (eligible()) giftsFor(shape()).catch(() => {}); }

  // ---------- the strip ----------
  let locked = false;                // true while the check dialog is saving the order
  // Called when the check dialog opens. Shows the strip (or keeps it hidden) and starts making the cards.
  async function load() {
    strip.hidden = true;
    if (!eligible()) { state = null; return; }
    let front = null, gifts = [];
    try {
      const out = await editor.exportDesign();
      const first = Object.keys(out.sides)[0];
      if (countObjects(out.sides[first]) > 0) front = { json: out.sides[first], area: out.areas[first], sources: out.sources, originals: out.originals };
      if (front) {
        const all = await giftsFor(shape());
        const areas = await Promise.all(all.map(giftArea));
        gifts = all.filter((g, i) => areas[i] && shapesMatch(front.area, areas[i]));
      }
    } catch (e) { console.error("RightGifting: the gifts could not be made", e); }
    if (!front || !gifts.length) { state = null; return; }          // nothing on the front, or no gift that fits

    const key = JSON.stringify([front.json, front.area, editor.product.handle]);
    if (!state || state.key !== key) {                               // a new or changed design: the gifts are made again
      const ticks = new Map(state ? state.cards.map((c) => [c.gift.handle, { ticked: c.ticked, sizeLabel: c.sizeLabel }]) : []);
      state = { key, cards: gifts.map((g) => ({ gift: g, status: "loading", ticked: !!(ticks.get(g.handle) || {}).ticked, sizeLabel: (ticks.get(g.handle) || {}).sizeLabel || "" })) };
      state.cards.forEach((card) => prepare(card, front));
    }
    strip.hidden = false;
    renderAll();
  }
  // The gifts to save with the design: the ticked cards that are ready.
  function selected() { return state ? state.cards.filter((c) => c.ticked && c.status === "ready").map(toExtra) : []; }
  // While the order is being saved the strip cannot change.
  function lock(on) { locked = !!on; grid.querySelectorAll("input, select, button").forEach((el) => { el.disabled = locked || el.hasAttribute("data-off"); }); }
  // What the check dialog and the upload need to know about a gift.
  const sizeOf = (card) => (card.offer && card.offer.info.sizes.find((s) => s.label === card.sizeLabel)) || null;
  function toExtra(card) {
    const info = card.offer && card.offer.info, size = sizeOf(card);
    return {
      shape: card.gift.shape, handle: card.gift.handle, title: card.gift.title || card.product.title,
      sizeLabel: size ? size.label : "", variantId: (size && size.variantId) || (info && !info.sizes.length && info.defaultVariantId) || "",
      preview: card.preview, product: card.product, sides: card.sides, design: card.design, sources: card.sources, originals: card.originals
    };
  }

  // ---------- making a card ----------
  // Loads the gift, places the design on it, draws the picture. The price and sizes arrive on their own and fill in when they are there.
  async function prepare(card, front) {
    getOffer(card.gift.handle).then((o) => {
      if (!alive(card)) return;
      card.offer = o;
      if (o && o.info.sizes.length && !sizeOf(card)) { const free = o.info.sizes.find((s) => !s.soldOut); card.sizeLabel = free ? free.label : ""; }
      if (card.status === "ready") paint(card);
      updateFoot(); if (card.ticked) onChange();
    });
    try {
      const { product, sides } = await loadGiftProduct(card.gift);
      const [x0, y0, x1, y1] = sides[0].area.bbox;
      const placed = placeDesign(front.json, front.area, { x: x0, y: y0, w: x1 - x0, h: y1 - y0 });      // the front design, in this gift's print line; other sides keep their original
      card.product = product; card.sides = sides;
      card.design = Object.fromEntries(sides.map((s, i) => [s.id, i === 0 ? placed : "[]"]));
      card.sources = new Map(front.sources); card.originals = new Map(front.originals);
      await draw(card);
    } catch (e) {
      console.error("RightGifting: a gift could not be loaded", e);
      if (!alive(card)) return;
      state.cards = state.cards.filter((c) => c !== card);                          // a gift that cannot be shown is left out
      if (!state.cards.length) strip.hidden = true;                                 // none could be loaded: no strip
      renderAll(); onChange();
      return;
    }
    if (!alive(card)) return;
    card.status = "ready";
    paint(card); updateFoot(); if (card.ticked) onChange();
  }
  // The picture of the card: the gift's photo with the design on it (front side).
  async function draw(card) {
    const { createHeadless, ensureSidePhotos } = await import("./headless.js");
    await ensureSidePhotos(card.sides, card.design);
    const h = await createHeadless({ product: card.product, sides: card.sides, design: card.design, sources: card.sources, originals: card.originals });
    try { card.preview = h.renderMock(0, 640); } finally { h.dispose(); }
  }

  // ---------- what the customer sees ----------
  const shortSize = (s) => `${s.label}${s.price ? " · " + formatPrice(s.price) : ""}${s.soldOut ? " (sold out)" : ""}`;
  function cardHtml(card, i) {
    if (card.status !== "ready") return `<li class="gift-card loading" data-i="${i}" aria-hidden="true"><div class="gift-img"></div><div class="gift-line"></div><div class="gift-line short"></div></li>`;
    const sizes = card.offer ? card.offer.info.sizes : [], title = shortTitle(card.gift.title || card.product.title);
    const soldOut = card.offer && (sizes.length ? sizes.every((s) => s.soldOut) : false);
    const price = card.offer && card.offer.from ? `from ${formatPrice(card.offer.from)}` : "";
    return `<li class="gift-card${card.ticked ? " on" : ""}" data-i="${i}">
      <img class="gift-img" alt="Your design on ${esc(title)}" src="${esc(card.preview)}">
      <p class="gift-title">${esc(title)}</p>
      ${price ? `<p class="gift-price">${esc(price)}</p>` : ""}
      <label class="gift-add"><input type="checkbox" data-tick ${card.ticked ? "checked" : ""} ${soldOut ? "disabled data-off" : locked ? "disabled" : ""} aria-label="Add ${esc(title)}"><span>${soldOut ? "Sold out" : "Add"}</span></label>
      ${sizes.length ? `<label class="gift-size"><span class="sr">Size for ${esc(title)}</span><select data-size aria-label="Size for ${esc(title)}" ${locked ? "disabled" : ""}>${sizes.map((s) => `<option value="${esc(s.label)}" ${s.soldOut ? "disabled" : ""} ${s.label === card.sizeLabel ? "selected" : ""}>${esc(shortSize(s))}</option>`).join("")}</select></label>` : ""}
      <button class="linkbtn" type="button" data-adjust ${locked ? "disabled" : ""}>Adjust</button>
    </li>`;
  }
  function renderAll() {
    if (!state) return;
    grid.innerHTML = state.cards.map(cardHtml).join("");
    updateFoot();
  }
  function paint(card) {
    const i = state.cards.indexOf(card), li = grid.children[i];
    if (!li) return;
    const focused = li.contains(document.activeElement) ? ["[data-size]", "[data-tick]", "[data-adjust]"].find((q) => document.activeElement.matches(q)) : "";
    li.outerHTML = cardHtml(card, i);
    const el = focused && grid.children[i].querySelector(focused);
    if (el) el.focus();
  }
  // The line over the strip: what one more gift would save ("Add 1 more gift, save 5%"). The discount already reached is in the order above.
  function updateFoot() {
    if (!state) return;
    const live = state.cards.filter((c) => c.status !== "failed");
    const n = live.filter((c) => c.ticked && c.status === "ready").length;
    const room = live.filter((c) => !c.ticked).length;
    const d = giftDiscount(1 + n, room);
    $("giftNudgeText").textContent = d.nudge;
    $("giftNudge").hidden = !d.nudge;
  }

  grid.addEventListener("change", (e) => {
    const li = e.target.closest(".gift-card"); if (!li || !state) return;
    const card = state.cards[Number(li.dataset.i)]; if (!card) return;
    if (e.target.matches("[data-tick]")) { card.ticked = e.target.checked; li.classList.toggle("on", card.ticked); }
    else if (e.target.matches("[data-size]")) card.sizeLabel = e.target.value;
    updateFoot(); onChange();
  });
  grid.addEventListener("click", (e) => {
    const b = e.target.closest("[data-adjust]"); if (!b || !state || locked) return;
    const card = state.cards[Number(b.closest(".gift-card").dataset.i)];
    if (card && card.status === "ready") adjust(card);
  });

  // ---------- Adjust ----------
  function adjust(card) {
    if (adjusting) return;
    adjusting = card;
    openAdjust({
      shop: ctx.shop, handle: card.gift.handle, title: card.gift.title || card.product.title,
      design: { sides: card.design, side: card.sides[0].id, sources: card.sources, originals: card.originals },
      onDone: async (d) => {
        card.design = { ...card.design, ...Object.fromEntries(Object.entries(d.sides).filter(([id]) => id in card.design)) };
        for (const [id, src] of d.sources) card.sources.set(id, src);
        for (const [id, file] of d.originals) card.originals.set(id, file);
        card.ticked = true;                                          // the customer worked on it: they want it
        try { await draw(card); } catch (e) { console.error("RightGifting: the gift picture could not be drawn again", e); }
        if (alive(card)) { paint(card); updateFoot(); onChange(); }
      },
      onClose: () => {
        adjusting = null;
        const li = alive(card) && grid.children[state.cards.indexOf(card)], b = li && li.querySelector("[data-adjust]");
        if (b) b.focus();
      }
    });
  }

  return { load, selected, lock, preload };
}
